import React, { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { mergeClasses } from '@fluentui/react-components';
import {
  useFloatingActionBarStyles,
  FLOATING_ACTION_BAR_EXIT_MS,
  FLOATING_ACTION_BAR_ROOT_LEFT_VAR,
  FLOATING_ACTION_BAR_ROOT_RIGHT_VAR,
  FLOATING_ACTION_BAR_INNER_MAX_WIDTH_VAR,
} from './FloatingActionBar.styles';

const NARROW_VIEWPORT_QUERY = '(max-width: 639px)';

export interface FloatingActionBarProps {
  visible: boolean;
  hideOnFocusInputs?: boolean;
  ariaLabel: string;
  children: ReactNode;
  /**
   * When true, the bar omits its own background, border, and padding so the
   * floating children (typically a self-styled card) render against the page
   * without an additional surface. Defaults to `false` (chrome on).
   */
  bare?: boolean;
  /**
   * Optional override for the inner content max-width. Defaults to the shared
   * 75rem inner cap. Pass a wider value (e.g., `100%`) when the floating
   * children need to align with a wider downstream surface such as the
   * Available Spaces card grid.
   */
  innerMaxWidth?: string;
  /**
   * Optional viewport offset for the bar's left edge (e.g., `'211px'`). When
   * provided together with `rootRight`, the bar pins to a specific horizontal
   * region of the viewport instead of spanning the full width. Used to anchor
   * the bar to the same horizontal extent as a downstream surface that does
   * not sit at the viewport's center (e.g., the Available Spaces card grid
   * which is offset by the persistent left navigation rail).
   */
  rootLeft?: string;
  /** Optional viewport offset for the bar's right edge. See `rootLeft`. */
  rootRight?: string;
}

function useHiddenForFocusedInput(enabled: boolean): boolean {
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') {
      setHidden(false);
      return;
    }

    const mql = window.matchMedia(NARROW_VIEWPORT_QUERY);
    let active = mql.matches;
    let focusOutTimeoutId: ReturnType<typeof setTimeout> | null = null;

    const updateFromFocus = () => {
      if (!active) {
        setHidden(false);
        return;
      }
      const el = document.activeElement;
      const focusedInput =
        !!el &&
        (el.tagName === 'INPUT' ||
          el.tagName === 'TEXTAREA' ||
          (el as HTMLElement).isContentEditable);
      setHidden(focusedInput);
    };

    const onFocusIn = () => updateFromFocus();
    const onFocusOut = () => {
      if (focusOutTimeoutId !== null) {
        clearTimeout(focusOutTimeoutId);
      }
      focusOutTimeoutId = setTimeout(() => {
        focusOutTimeoutId = null;
        updateFromFocus();
      }, 0);
    };
    const onMediaChange = (event: MediaQueryListEvent) => {
      active = event.matches;
      updateFromFocus();
    };

    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);
    mql.addEventListener('change', onMediaChange);
    updateFromFocus();

    return () => {
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', onFocusOut);
      mql.removeEventListener('change', onMediaChange);
      if (focusOutTimeoutId !== null) {
        clearTimeout(focusOutTimeoutId);
        focusOutTimeoutId = null;
      }
    };
  }, [enabled]);

  return hidden;
}

export const FloatingActionBar: React.FC<FloatingActionBarProps> = ({
  visible,
  hideOnFocusInputs = false,
  ariaLabel,
  children,
  bare = false,
  innerMaxWidth,
  rootLeft,
  rootRight,
}) => {
  const styles = useFloatingActionBarStyles();
  const hiddenByInputFocus = useHiddenForFocusedInput(hideOnFocusInputs && visible);

  // Anchored mode is engaged when the consumer supplies either viewport edge
  // offset; the root drops its bare-mode horizontal padding so the inner
  // exactly spans the requested region.
  const anchored = rootLeft !== undefined || rootRight !== undefined;

  // Dynamic positioning + sizing is delivered through CSS custom properties
  // declared in FloatingActionBar.styles.ts. The component only assigns the
  // variables; no CSS property values appear in JSX. This keeps all CSS
  // declarations in the styles file per the project's styling conventions.
  const rootCssVars: CSSProperties | undefined =
    rootLeft !== undefined || rootRight !== undefined || innerMaxWidth !== undefined
      ? ({
          ...(rootLeft !== undefined && {
            [FLOATING_ACTION_BAR_ROOT_LEFT_VAR]: rootLeft,
          }),
          ...(rootRight !== undefined && {
            [FLOATING_ACTION_BAR_ROOT_RIGHT_VAR]: rootRight,
          }),
          ...(innerMaxWidth !== undefined && {
            [FLOATING_ACTION_BAR_INNER_MAX_WIDTH_VAR]: innerMaxWidth,
          }),
        } as CSSProperties)
      : undefined;

  // Effective show/hide state combines the consumer's `visible` flag with the
  // mobile keyboard hide-on-focus behavior.
  const shouldShow = visible && !hiddenByInputFocus;

  // Keep the bar mounted briefly after `shouldShow` flips to false so the
  // slide-down exit animation can play before the node is removed.
  const [mounted, setMounted] = useState<boolean>(shouldShow);
  const [phase, setPhase] = useState<'enter' | 'exit'>(shouldShow ? 'enter' : 'exit');
  const exitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (shouldShow) {
      if (exitTimerRef.current) {
        clearTimeout(exitTimerRef.current);
        exitTimerRef.current = null;
      }
      setMounted(true);
      setPhase('enter');
      return;
    }
    if (!mounted) return;
    setPhase('exit');
    exitTimerRef.current = setTimeout(() => {
      setMounted(false);
      exitTimerRef.current = null;
    }, FLOATING_ACTION_BAR_EXIT_MS);
    return () => {
      if (exitTimerRef.current) {
        clearTimeout(exitTimerRef.current);
        exitTimerRef.current = null;
      }
    };
  }, [shouldShow, mounted]);

  // During the exit phase, ensure the subtree cannot trap focus or be reached
  // by sequential keyboard navigation. Blur any focused descendant immediately
  // so screen reader/keyboard users do not interact with an aria-hidden region.
  useEffect(() => {
    if (phase !== 'exit' || typeof document === 'undefined') return;
    const activeEl = document.activeElement as HTMLElement | null;
    if (activeEl && rootRef.current?.contains(activeEl)) {
      activeEl.blur?.();
    }
  }, [phase]);

  if (!mounted) {
    return null;
  }

  const isExiting = phase === 'exit';

  return (
    <div
      ref={rootRef}
      role="region"
      aria-label={ariaLabel}
      aria-hidden={isExiting ? true : undefined}
      tabIndex={isExiting ? -1 : undefined}
      {...(isExiting ? ({ inert: true } as Record<string, unknown>) : {})}
      className={mergeClasses(
        styles.root,
        bare ? styles.rootBare : styles.rootWithChrome,
        anchored ? styles.rootAnchored : undefined,
        phase === 'enter' ? styles.enter : styles.exit
      )}
      style={rootCssVars}
      data-testid="floating-action-bar"
      data-phase={phase}
    >
      <div className={styles.inner}>{children}</div>
    </div>
  );
};

FloatingActionBar.displayName = 'FloatingActionBar';
